/**
 * The words for the profile's required fields and their validation messages —
 * one mapping for the web and mobile "complete your profile" forms and the
 * CheckUp's profile card.
 *
 * Every key goes through a LITERAL `t()` so the key-usage gate can prove each
 * one exists; the `switch`es are exhaustive over the server's field union, so a
 * new required field on the server is a compile error here rather than a raw
 * slug on screen.
 */
import {
  PHONE_DIGITS_MAX,
  PHONE_DIGITS_MIN,
  PROFILE_MIN_AGE,
  type ProfileFieldInput,
} from '@tonnie/schemas';
import type { ProfileRequiredField } from '@tonnie/types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The chip label for one required field — the CheckUp gate's own words. */
export function profileFieldLabel(t: Translate, field: ProfileRequiredField): string {
  switch (field) {
    case 'name':
      return t('checkup.profileGate.field.name');
    case 'date_of_birth':
      return t('checkup.profileGate.field.dateOfBirth');
    case 'nationality':
      return t('checkup.profileGate.field.nationality');
    case 'current_country':
      return t('checkup.profileGate.field.currentCountry');
    case 'current_city':
      return t('checkup.profileGate.field.currentCity');
    case 'phone':
      return t('checkup.profileGate.field.phone');
    default: {
      const unhandled: never = field;
      return unhandled;
    }
  }
}

/**
 * A `profileFieldSchemas` message (an i18n key) rendered with the number it
 * names. An unknown message — a schema change nobody mapped here — renders the
 * generic "required", never a raw key or English.
 */
export function profileFieldErrorText(t: Translate, message: string): string {
  switch (message) {
    case 'onboarding.validation.firstName_required':
      return t('onboarding.validation.firstName_required');
    case 'onboarding.validation.firstName_max_length':
      return t('onboarding.validation.firstName_max_length');
    case 'onboarding.validation.lastName_required':
      return t('onboarding.validation.lastName_required');
    case 'onboarding.validation.lastName_max_length':
      return t('onboarding.validation.lastName_max_length');
    case 'onboarding.validation.dateOfBirth_required':
      return t('onboarding.validation.dateOfBirth_required');
    case 'onboarding.validation.dateOfBirth_invalid_format':
      return t('onboarding.validation.dateOfBirth_invalid_format');
    case 'onboarding.validation.dateOfBirth_min_age':
      return t('onboarding.validation.dateOfBirth_min_age', { minAge: PROFILE_MIN_AGE });
    case 'onboarding.validation.dateOfBirth_invalid':
      return t('onboarding.validation.dateOfBirth_invalid');
    case 'onboarding.validation.citizenship_required':
      return t('onboarding.validation.citizenship_required');
    case 'validation.originCountry.required':
      return t('validation.originCountry.required');
    case 'validation.countryCode.length':
      return t('validation.countryCode.length');
    case 'validation.city.required':
      return t('validation.city.required');
    case 'validation.city.tooLong':
      return t('validation.city.tooLong');
    case 'onboarding.validation.phone_required':
      return t('onboarding.validation.phone_required');
    case 'onboarding.validation.phone_invalid_characters':
      return t('onboarding.validation.phone_invalid_characters');
    case 'onboarding.validation.phone_min_digits':
      return t('onboarding.validation.phone_min_digits', { minDigits: PHONE_DIGITS_MIN });
    case 'onboarding.validation.phone_max_digits':
      return t('onboarding.validation.phone_max_digits', { maxDigits: PHONE_DIGITS_MAX });
    default:
      return t('validation.required');
  }
}

/**
 * A stored name worth pre-filling in a profile form. The server writes the
 * literal `'Unknown'` when the identity provider supplies no name and counts
 * it as missing (`ProfileCompletenessService.filled`); showing it in the input
 * would ask the citizen to correct a placeholder they never typed.
 */
export function storedProfileName(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed.toLowerCase() === 'unknown' ? '' : trimmed;
}

/**
 * The inputs each required field is answered with — the focused profile form
 * on both apps asks for exactly these, for exactly the missing fields.
 */
export const PROFILE_FIELD_INPUTS: Readonly<
  Record<ProfileRequiredField, readonly ProfileFieldInput[]>
> = {
  name: ['firstName', 'lastName'],
  date_of_birth: ['dateOfBirth'],
  nationality: ['citizenships'],
  current_country: ['currentCountry'],
  current_city: ['currentCity'],
  phone: ['phoneCountryCode', 'phone'],
};
